import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Spin, Empty, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, ReferenceLine, CartesianGrid
} from 'recharts';
import labTestService from '../services/labTestService';
import { localizedName } from '../utils/labFlags';
import { FLAG_COLORS, flagText } from './VisitLabTestCards';
import './VisitLabTestCards.css';

const { Text } = Typography;

// One field's values across a patient's visits: the creatinine over the last
// year, as a line with the normal band shaded behind it, and the same points
// as a table underneath for the doctor who wants the numbers.
//
//   patientId  whose values
//   fieldId    which field; null closes the modal
//   label      what to call it in the title (already translated)
const LabTrendModal = ({ patientId, fieldId, label, onClose }) => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState([]);
    const [field, setField] = useState(null);

    useEffect(() => {
        if (!patientId || !fieldId) return undefined;
        let cancelled = false;
        setLoading(true);
        labTestService.getTrend(patientId, fieldId)
            .then((response) => {
                if (cancelled) return;
                setPoints(response.data || []);
                setField(response.field || null);
            })
            .catch(() => {
                if (!cancelled) {
                    setPoints([]);
                    setField(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [patientId, fieldId]);

    const numeric = points.filter((p) => typeof p.value === 'number');
    const data = numeric.map((p) => ({
        date: dayjs(p.visitDate).format('YYYY-MM-DD'),
        value: p.value,
        flag: p.flag
    }));
    const range = field?.range || { low: null, high: null };
    const unit = field?.unit || (points[0] && points[0].unit) || '';
    const title = field && field.label ? localizedName(field, i18n.language) : label;

    // The axis has to show the normal band even when every value sits outside it.
    const values = data.map((d) => d.value).concat(
        range.low !== null ? [range.low] : [],
        range.high !== null ? [range.high] : []
    );
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;
    const pad = (max - min) * 0.15 || 1;

    const columns = [
        { title: t('date'), dataIndex: 'visitDate', render: (v) => dayjs(v).format('YYYY-MM-DD') },
        {
            title: t('result'),
            dataIndex: 'value',
            render: (v, row) => (
                <span className={row.flag ? `lab-results-row-${row.flag}` : ''}>
                    <span className="lab-results-value">{String(v)}</span>{row.unit ? ` ${row.unit}` : ''}
                </span>
            )
        },
        {
            title: t('labFlag') || 'Flag',
            dataIndex: 'flag',
            render: (flag) => (flag ? <Tag color={FLAG_COLORS[flag]}>{flagText(t, flag)}</Tag> : null)
        }
    ];

    return (
        <Modal
            open={Boolean(fieldId)}
            onCancel={onClose}
            footer={null}
            width={640}
            title={`${t('labTrend') || 'Trend'}: ${title}${unit ? ` (${unit})` : ''}`}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
            ) : points.length === 0 ? (
                <Empty description={t('noData')} />
            ) : (
                <>
                    <div className="lab-trend-meta">
                        {(range.low !== null || range.high !== null) && (
                            <Text type="secondary">
                                {t('referenceRange')}: {range.low !== null ? range.low : '…'} – {range.high !== null ? range.high : '…'}{unit ? ` ${unit}` : ''}
                            </Text>
                        )}
                        <Text type="secondary">{t('visits')}: {points.length}</Text>
                    </div>
                    {data.length > 0 && (
                        <div className="lab-trend-chart" dir="ltr">
                            <ResponsiveContainer>
                                <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" />
                                    <XAxis dataKey="date" fontSize={11} />
                                    <YAxis domain={[min - pad, max + pad]} fontSize={11} width={48} tickFormatter={(v) => Number(v.toFixed(2))} />
                                    <Tooltip formatter={(value) => [`${value}${unit ? ` ${unit}` : ''}`, title]} />
                                    {range.low !== null && range.high !== null && (
                                        <ReferenceArea y1={range.low} y2={range.high} fill="#52c41a" fillOpacity={0.08} />
                                    )}
                                    {range.low !== null && <ReferenceLine y={range.low} stroke="#52c41a" strokeDasharray="4 4" />}
                                    {range.high !== null && <ReferenceLine y={range.high} stroke="#52c41a" strokeDasharray="4 4" />}
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#05668D"
                                        strokeWidth={2}
                                        dot={({ cx, cy, payload }) => (
                                            <circle
                                                key={`${payload.date}-${cx}`}
                                                cx={cx}
                                                cy={cy}
                                                r={4}
                                                fill={payload.flag === 'high' || payload.flag === 'abnormal' ? '#cf1322'
                                                    : payload.flag === 'low' ? '#1677ff' : '#05668D'}
                                            />
                                        )}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <Table
                        size="small"
                        columns={columns}
                        dataSource={[...points].reverse()}
                        rowKey={(row) => `${row.visitLabTestId}-${row.visitDate}`}
                        pagination={points.length > 8 ? { pageSize: 8 } : false}
                    />
                </>
            )}
        </Modal>
    );
};

export default LabTrendModal;
